import React, { useState, useEffect, useCallback, useRef } from "react";
import { useCompany } from "../../../contexts/CompanyContext";
import { authService, companyApi } from "../../../services";
import type { Company, CompanyUpdatePayload } from "../../../types";
import { resizeImageFileToJpegDataUrl } from "../../../utils/imageResize";
import { cnpjToApiPayload, formatCnpjMask } from "../../../utils/cnpjUtils";
import { cepToApiPayload, formatCepMask } from "../../../utils/cepUtils";
import {
  Card,
  EmptyState,
  ErrorState,
  Input,
  FormActions,
  ConfirmationModal,
  IconsLarge,
  ActionButton,
  Loading,
} from "../../shared";
import { AdminPageHeader } from "../shared";

const MAX_LOGO_UPLOAD_BYTES = 8 * 1024 * 1024;

function emptyToNull(s: string): string | null {
  const t = s.trim();
  return t === "" ? null : t;
}

function parseEmployeeCount(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = parseInt(t, 10);
  return Number.isNaN(n) ? null : n;
}

/** `undefined` = não alterar logo; `string` = novo data URL; `null` = remover. */
type LogoOverride = string | null | undefined;

function mergeCompany(prev: Company, data: Company): Company {
  return { ...prev, ...data };
}

export const CompanyUpdate: React.FC = () => {
  const { company, updateCompany } = useCompany();
  const updateCompanyRef = useRef(updateCompany);
  updateCompanyRef.current = updateCompany;
  const [name, setName] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [address, setAddress] = useState("");
  const [cep, setCep] = useState("");
  const [city, setCity] = useState("");
  const [ceo, setCeo] = useState("");
  const [employeeCountStr, setEmployeeCountStr] = useState("");
  const [businessArea, setBusinessArea] = useState("");
  const [logoOverride, setLogoOverride] = useState<LogoOverride>(undefined);
  const [logoBusy, setLogoBusy] = useState(false);
  const logoFileInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const resetFormFromCompany = useCallback((c: Company) => {
    setName(c.name);
    setCnpj(formatCnpjMask(c.cnpj ?? ""));
    setAddress(c.address ?? "");
    setCep(formatCepMask(c.cep ?? ""));
    setCity(c.city ?? "");
    setCeo(c.ceo ?? "");
    setEmployeeCountStr(
      c.employeeCount != null && !Number.isNaN(Number(c.employeeCount)) ? String(c.employeeCount) : ""
    );
    setBusinessArea(c.businessArea ?? "");
    setLogoOverride(undefined);
  }, []);

  useEffect(() => {
    if (company) resetFormFromCompany(company);
  }, [company, resetFormFromCompany]);

  useEffect(() => {
    const id = company?.id;
    if (id == null) return;
    let cancelled = false;
    setDetailLoading(true);
    setError(null);
    void (async () => {
      const response = await companyApi.getCompanyById(id);
      if (cancelled) return;
      if (response.success && response.data) {
        const fresh = response.data as Company;
        const stored = authService.getCompanyData();
        const merged =
          stored?.id === fresh.id ? mergeCompany(stored, fresh) : fresh;
        updateCompanyRef.current(merged);
      } else {
        setError(response.error ?? "Não foi possível carregar os dados da empresa.");
      }
      if (!cancelled) setDetailLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [company?.id]);

  const buildPayload = (): CompanyUpdatePayload => {
    if (!company) return {};
    const payload: CompanyUpdatePayload = {
      name: name.trim(),
      cnpj: cnpjToApiPayload(cnpj),
      address: emptyToNull(address),
      cep: cepToApiPayload(cep),
      city: emptyToNull(city),
      ceo: emptyToNull(ceo),
      employeeCount: parseEmployeeCount(employeeCountStr),
      businessArea: emptyToNull(businessArea),
    };
    if (logoOverride !== undefined) {
      payload.logo = logoOverride;
    }
    return payload;
  };

  const handleSubmit = async () => {
    if (!company) return;
    if (!name.trim()) {
      setError("Nome da empresa é obrigatório.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await companyApi.updateCompany(company.id, buildPayload());
      if (response.success && response.data) {
        const merged = mergeCompany(company, response.data as Company);
        updateCompany(merged);
        setLogoOverride(undefined);
      } else {
        setError(response.error ?? "Falha ao atualizar empresa.");
      }
    } catch {
      setError("Falha ao atualizar empresa.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!company) return;
    setDeactivating(true);
    setError(null);
    try {
      const response = await companyApi.updateCompany(company.id, {
        isActive: false,
      });
      if (response.success && response.data) {
        const merged = mergeCompany(company, response.data as Company);
        updateCompany(merged);
        setShowDeactivateModal(false);
      } else {
        setError(response.error ?? "Falha ao desativar empresa.");
      }
    } catch {
      setError("Falha ao desativar empresa.");
    } finally {
      setDeactivating(false);
    }
  };

  const onLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_LOGO_UPLOAD_BYTES) {
      setError("Imagem muito grande (máx. 8 MB). Escolha outro arquivo.");
      return;
    }
    setLogoBusy(true);
    setError(null);
    try {
      const dataUrl = await resizeImageFileToJpegDataUrl(file, 512);
      setLogoOverride(dataUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível processar a imagem.");
    } finally {
      setLogoBusy(false);
    }
  };

  const logoPreviewSrc =
    logoOverride === undefined ? company?.logo ?? undefined : logoOverride || undefined;

  if (!company) {
    return (
      <div className="space-y-8 animate-fade-in">
        <AdminPageHeader
          icon={<IconsLarge.Company />}
          title="Dados da empresa"
          subtitle="Atualize as informações da empresa"
          showViewToggle={false}
        />
        <Card className="p-8">
          <EmptyState
            icon={<IconsLarge.Company />}
            title="Nenhuma empresa selecionada"
            description="Selecione uma empresa no menu acima para editar os dados."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<IconsLarge.Company />}
        title="Empresa"
        subtitle="Atualize as informações da empresa"
        showViewToggle={false}
      />

      {error && (
        <ErrorState
          message={error}
          variant="card"
          onRetry={() => setError(null)}
        />
      )}

      <Card className="p-8">
        <div className="max-w-3xl space-y-8">
          {detailLoading ? (
            <Loading
              size="sm"
              variant="spinner"
              message="Carregando dados da empresa…"
              className="py-2"
            />
          ) : null}
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 pb-6 border-b border-gray-200 dark:border-[#374151]">
            <div className="h-24 w-24 rounded-xl border border-gray-200 dark:border-[#4B5563] bg-gray-50 dark:bg-[#2D3748] overflow-hidden flex items-center justify-center shrink-0">
              {logoPreviewSrc ? (
                <img src={logoPreviewSrc} alt="" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-xs text-gray-400 dark:text-slate-500 px-1 text-center">Sem logo</span>
              )}
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-sm font-medium text-gray-800 dark:text-slate-200">Logo</p>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Envie PNG ou JPEG. A imagem é redimensionada (lado máx. 512 px) e enviada em base64 (JPEG).
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <input
                  ref={logoFileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  aria-hidden
                  tabIndex={-1}
                  disabled={saving || logoBusy}
                  onChange={onLogoFile}
                />
                <ActionButton
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={saving || logoBusy}
                  onClick={() => logoFileInputRef.current?.click()}
                >
                  {logoBusy ? "Processando…" : "Carregar imagem"}
                </ActionButton>
                {logoOverride === null && company.logo ? (
                  <ActionButton
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={saving || logoBusy}
                    onClick={() => setLogoOverride(undefined)}
                  >
                    Restaurar logo
                  </ActionButton>
                ) : null}
                {(typeof logoOverride === "string" || (logoOverride === undefined && Boolean(company.logo))) && (
                  <ActionButton
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={saving || logoBusy}
                    onClick={() => setLogoOverride(null)}
                  >
                    Remover logo
                  </ActionButton>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="sm:col-span-2">
              <Input
                label="Nome da empresa"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Minha Empresa"
                disabled={saving}
                required
              />
            </div>
            <Input
              label="CNPJ"
              type="text"
              value={cnpj}
              onChange={(e) => setCnpj(formatCnpjMask(e.target.value))}
              placeholder="00.000.000/0000-00"
              disabled={saving}
            />
            <Input
              label="CEP"
              type="text"
              value={cep}
              onChange={(e) => setCep(formatCepMask(e.target.value))}
              placeholder="00000-000"
              disabled={saving}
            />
            <div className="sm:col-span-2">
              <Input
                label="Endereço"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Rua, número, complemento"
                disabled={saving}
              />
            </div>
            <Input
              label="Cidade"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Cidade"
              disabled={saving}
            />
            <Input
              label="Responsável (CEO)"
              type="text"
              value={ceo}
              onChange={(e) => setCeo(e.target.value)}
              placeholder="Nome"
              disabled={saving}
            />
            <Input
              label="Número de funcionários"
              type="number"
              min={0}
              value={employeeCountStr}
              onChange={(e) => setEmployeeCountStr(e.target.value)}
              placeholder="Opcional"
              disabled={saving}
            />
            <Input
              label="Área de atuação"
              type="text"
              value={businessArea}
              onChange={(e) => setBusinessArea(e.target.value)}
              placeholder="Ex: Comércio varejista"
              disabled={saving}
            />
          </div>

          <FormActions
            onCancel={() => {
              resetFormFromCompany(company);
              setError(null);
            }}
            onSubmit={handleSubmit}
            loading={saving}
            isEditing
            submitType="button"
            submitText="Salvar alterações"
          />

          {company.isActive !== false && (
            <div className="pt-6 border-t border-gray-200 dark:border-[#374151]">
              <button
                type="button"
                onClick={() => setShowDeactivateModal(true)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg transition-colors disabled:opacity-50"
              >
                Desativar empresa
              </button>
            </div>
          )}
        </div>
      </Card>

      <ConfirmationModal
        isOpen={showDeactivateModal}
        title="Desativar empresa"
        message="Tem certeza que deseja desativar esta empresa? Você poderá reativá-la depois se necessário."
        confirmText="Desativar"
        cancelText="Cancelar"
        onConfirm={handleDeactivate}
        onCancel={() => setShowDeactivateModal(false)}
        loading={deactivating}
      />
    </div>
  );
};

export default CompanyUpdate;
